import React, { Component } from 'react';
import FadeIn from 'react-fade-in';
import _ from 'underscore';

import { Header } from '../../components/Header';
import Accordion from '../../components/Accordion';
import FieldWithHistory from '../../components/FieldWithHistory';
import FloatingButton from '../../components/FloatingButton';
import FullNameForm from '../../components/FullNameForm';
import LoaderButton from '../../components/LoaderButton';
import GenericTable from '../../components/GenericTable';
import Switch from '../../components/Switch';
import CustomDatePicker, {
  DateRangeOptions,
} from '../../components/CustomDatePicker';


import {
  usernameValidate,
  isValidEmail,
  uuidValidate,
  generateFilterRegex,
  updateLogData,
  calculateStartAndEndDate,
} from '../../libs/utils';
import {
  getUserSummary,
  getUserLog,
  getOEMRegionList,
  getUserSummaryByProperty,
  recordAcceptedTrustXPI,
  getUserNames,
  getShopNames,
} from '../../libs/db-lib';
import {
  baseLogColumns,
} from '../../libs/tables/columnDefinitions';

import { createUserInfoCard } from '../../libs/ui-constructors';

import {
  getRecentSearches,
  setRecentSearches,
} from '../../libs/utils-ts';

import {
  ChildProps,
  QueriedUser,
  Shop,
  Log,
  FullNameQuery,
} from '../../types';
import { ExternalLogSubcomponent, LogSubcomponent } from '../../libs/tables/subComponents';
import CopyData from '../../components/CopyData';
import ShopInfoAccordion from '../ShopFunctions/ShopInfoAccordion';

interface GetUserSummaryState {
  queryType: string;
  query: string;
  queryError: string;
  isLoading: boolean;
  fullNameQuery: FullNameQuery;
  queriedUser: QueriedUser;
  queriedUsers: QueriedUser[];
  queriedUsersFiltered: QueriedUser[];
  filter: string;
  showSelectUser: boolean;
  includeLog: boolean;
  windowWidth: number;
  log: Log[];
  showLog: boolean;
  showUserInfo: boolean;
  rangePicked: DateRangeOptions;
}

let state: GetUserSummaryState = {
  queryType: 'USERNAME',
  query: '',
  queryError: '',
  isLoading: false,
  fullNameQuery: {
    firstName: '',
    lastName: '',
  } as FullNameQuery,
  queriedUser: {
    userID: '',
    userName: '',
    firstName: '',
    lastName: '',
    email: '',
    userState: '',
    createdOn: '',
    language: '',
    certifiedTech: false,
    idVerified: false,
    phoneVerified: false,
    userShops: [] as Shop[],
  },
  queriedUsers: [] as QueriedUser[],
  queriedUsersFiltered: [] as QueriedUser[],
  filter: '',
  windowWidth: 0,
  showSelectUser: false,
  includeLog: false,
  log: [] as Log[],
  showLog: false,
  showUserInfo: false,
  rangePicked: 'last30Days',
};

const topRef = React.createRef<HTMLDivElement>();
export default class GetUserSummary extends Component<
  ChildProps,
  GetUserSummaryState
> {
  constructor(props: ChildProps) {
    super(props);
    const searchParams = new URLSearchParams(window.location.search);
    const query = searchParams.get('query');
    const queryType = searchParams.get('queryType');
    if (query && queryType) {
      state.query = query;
      state.queryType = queryType;
    }

    // This will initial state on first time, but stores previous state
    // when navigating back to the page
    this.state = state;
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    if (this.props.location !== prevProps.location) {
      // URL has changed
      this.checkURLChange();
    }
  }

  checkURLChange() {
    const searchParams = new URLSearchParams(window.location.search);
    const query = searchParams.get('query')?.replace(/ /g, '+');
    const queryType = searchParams.get('queryType');
    const userID = localStorage.getItem('userID');
    if (query && queryType) {
      this.setState({
        query,
        queryType,
      });
      this.handleSubmit(undefined, query, queryType);
    } else if (userID) {
      this.setState({
        query: userID,
        queryType: 'USER ID',
      });
      localStorage.removeItem('userID');
      this.handleSubmit(undefined, userID, 'USER ID');
    }
  }

  async componentDidMount() {
    this.checkURLChange();
    this.setState({ windowWidth: window.outerWidth });
    window.addEventListener('resize', this.setWindowWidth);
    window.localStorage.setItem(
      'pageData',
      JSON.stringify(
        {
          userInfo: this.state.queriedUser,
          userLog: this.state.log,
        },
        null,
        2
      )
    );
  }

  componentWillUnmount() {
    // Setting state (global file variable) to this.state allowed persistance
    // of state when navigating back to the component later
    state = {
      ...this.state,
      isLoading: false,
    };
  }

  setWindowWidth = () => {
    setTimeout(this.setStateWidth.bind(this), 20);
  };

  setStateWidth() {
    this.setState({ windowWidth: window.outerWidth });
    if (this.state.windowWidth <= 500 && document.querySelectorAll('.rccs')) {
      (
        document.querySelectorAll('.rccs') as NodeListOf<HTMLDivElement>
      ).forEach((element) => {
        element.style.transform = `scale(${this.state.windowWidth / 500})`;
      });
    }
  }

  handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | string,
    fieldId?: string
  ) => {
    if (typeof event === 'string') {
      let error = '';
      switch (this.state.queryType) {
        case 'USERNAME':
          error = !usernameValidate(event.trim())
            ? 'Username must be at least 8 alphanumeric lowercase characters starting with a letter'
            : '';
          event = event.toLowerCase();
          break;
        case 'EMAIL':
          error = !isValidEmail(event.trim())
            ? 'Email format is not valid!'
            : '';
          event = event.toLowerCase();
          break;
        case 'USER ID':
          error = !uuidValidate(event.trim())
            ? 'User ID must be in this format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
            : '';
          event = event.toLowerCase();
          break;
      }
      this.setState({
        [fieldId as any]: event,
        queryError: error,
      } as any);
    } else if (event) {
      let eventValue = event.target.value;
      this.setState({
        [event.target.id]: eventValue,
        queryError: '',
      } as any);
    }
  };

  handleSelectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    let eventValue = e.target.value;
    if (eventValue === 'EMAIL') {
      this.setState((prevState) => ({
        query: prevState.query.toLowerCase(),
      }));
    }

    this.setState({
      queryType: eventValue,
      queryError: '',
      query: '',
    });
  };

  handleFullNameChange = (fullNameObj: FullNameQuery) => {
    this.setState({
      fullNameQuery: fullNameObj,
    });
  };

  handleChangeFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      filter: e.target.value,
    });

    const updateFilter = _.debounce((f: string) => {
      this.updateFilteredList(f);
    }, 300);

    updateFilter(e.target.value);
  };

  updateFilteredList = (filter: string) => {
    let userList = this.state.queriedUsers;

    if (filter.trim().length > 0) {
      const regexStr = generateFilterRegex(filter);
      userList = this.state.queriedUsers.filter((u) => {
        return Object.values(u).some((value) => regexStr.test(value));
      });
    }
    this.setState({
      queriedUsersFiltered: userList || [],
    });
  };

  validateForm() {
    const { query, queryType, fullNameQuery } = this.state;
    switch (queryType) {
      case 'USERNAME':
        // 07/24/2024 JBump commented this out so that Enterprise
        //            Directory Services user can be searched
        // return usernameValidate(query);
        return true;
      case 'EMAIL':
        return isValidEmail(query);
      case 'USER ID':
        // 07/24/2024 JBump commented this out so that Enterprise
        //            Directory Services user can be searched
        // return uuidValidate(query);
        return true;
      case 'FIRST/LAST NAME':
        return fullNameQuery.firstName || fullNameQuery.lastName;
      default:
        return query.trim().length > 0;
    }
  }

  handleSubmit = async (
    e?: React.SyntheticEvent,
    query?: string,
    queryType?: string,
    queryBySelectUser?: string
  ) => {
    e?.preventDefault();

    this.setState({ isLoading: true, showLog: false, showSelectUser: false });
    if (!queryType) {
      queryType = this.state.queryType;
    }
    if (!query) {
      query = this.state.query;
    }
    let { fullNameQuery } = this.state;
    let result: any = null;
    if (queryBySelectUser) {
      queryType = 'USER ID';
      query = queryBySelectUser;
    }

    if (queryType === 'USER ID') {
      queryType = 'USERID';
    }

    if (queryType === 'USERNAME') {
      query = query!.toLowerCase();
    }

    if (queryType === 'FIRST/LAST NAME') {
      const usersByFullName = await getUserSummaryByProperty(
        'FIRST/LAST NAME',
        fullNameQuery
      );


      this.setState({
        queriedUsers: usersByFullName,
        queriedUsersFiltered: usersByFullName,
        showUserInfo: false,
        showLog: false,
        showSelectUser: true,
        isLoading: false,
        query: '',
      });

      let firstNameRecentSearches = getRecentSearches('firstName');
      let lastNameRecentSearches = getRecentSearches('lastName');

      if (firstNameRecentSearches) {
        if (firstNameRecentSearches.length > 4) firstNameRecentSearches.pop();

        if (fullNameQuery.firstName) {
          if (
            firstNameRecentSearches.indexOf(
              fullNameQuery.firstName as string
            ) === -1
          ) {
            firstNameRecentSearches.unshift(fullNameQuery.firstName as string);
          }
        }
      } else {
        if (fullNameQuery.firstName) {
          firstNameRecentSearches = [fullNameQuery.firstName];
        }
      }

      if (lastNameRecentSearches) {
        if (lastNameRecentSearches.length > 4) lastNameRecentSearches.pop();

        if (fullNameQuery.lastName) {
          if (
            lastNameRecentSearches.indexOf(fullNameQuery.lastName as string) ===
            -1
          ) {
            lastNameRecentSearches.unshift(fullNameQuery.lastName as string);
          }
        }
      } else {
        if (fullNameQuery.lastName) {
          lastNameRecentSearches = [fullNameQuery.lastName];
        }
      }
      setRecentSearches('firstName', firstNameRecentSearches);
      setRecentSearches('lastName', lastNameRecentSearches);
      return;
    }

    result = await getUserSummary(queryType, query);

    if (!result || Object.hasOwn(result, 'error')) {
      if (result && [401, 403].indexOf(result.error?.status) === -1) {
        this.props.handleShowAlert('Error', result.error);
      }
      this.setState({
        showUserInfo: false,
        isLoading: false,
      });
      return;
    } else {
      let pageData: { [k: string]: any } = {};
      pageData['userInfo'] = result;
      if (this.state.includeLog) {
        const { startDateStr, endDateStr } = calculateStartAndEndDate(
          this.state.rangePicked
        );
        const logs = await getUserLog(result.userID, startDateStr, endDateStr);
        const shopIDs: string[] = [];
        const userIDs: string[] = [];

        // Build list of shopIDs, userIDs, and toolIDs without duplicates
        logs.forEach((log) => {
          if (log.shopID && !shopIDs.includes(log.shopID)) {
            shopIDs.push(log.shopID);
          }
          if (log.userID && !userIDs.includes(log.userID)) {
            userIDs.push(log.userID);
          }
        });

        // Retrieve shops, users, and tools info
        const promises = [
          getShopNames(shopIDs),
          getUserNames(userIDs),
        ];
        
        const results = await Promise.all(promises);
        
        const shopNames = results[0];
        const userNames = results[1];

        // Inject shop and user names into logs
        logs.forEach((log) => {
          if (log.shopID) {
            log.shopName = shopNames[log.shopID];
          }
          if (log.userID) {
            log.userName = userNames[log.userID];
          }
        });

        const oemRegionList = await getOEMRegionList();
        const oemInfoAux = {};
        oemRegionList.forEach((element: any) => {
          oemInfoAux[element.oemID] = element.oemName;
          oemInfoAux[element.regionCode] = element.regionName;
        });

        await updateLogData(logs, oemInfoAux);

        this.setState({
          log: logs,
          showLog: true,
        });

        pageData['userLog'] = logs;
      }
      this.setState({
        queriedUser: result,
        showUserInfo: true,
        isLoading: false,
      });
      window.localStorage.setItem(
        'pageData',
        JSON.stringify(pageData, null, 2)
      );
    }
    this.setState({
      isLoading: false,
    });

    this.setStateWidth(); // ensures resizing of Card component to fit screen

    let recentSearches = getRecentSearches(queryType);

    if (recentSearches) {
      if (recentSearches.length > 4) recentSearches.pop();
      if (recentSearches.indexOf(query) === -1) recentSearches.unshift(query);
    } else {
      recentSearches = [query];
    }
    setRecentSearches(queryType, recentSearches);
  };

  handleAcceptPI = async (userID, processInstanceId) => {
    const self = this;

    this.setState({
      isLoading: true,
    });
    try {
      const results = await recordAcceptedTrustXPI(userID, processInstanceId);
      this.props.handleShowAlert(
        'Success',
        'Process Instance Acceptance has been recorded'
      );
      self.setState({
        isLoading: false,
      });
      this.handleSubmit(undefined, this.state.query, this.state.queryType);
    } catch (e) {
      if (Object.hasOwn(e, 'error')) {
        if ([401, 403].indexOf(e.error.status) === -1) {
          this.props.handleShowAlert(
            'Error',
            'Process Instance could not be recorded as accepted: ' + e
          );
        }
        self.setState({
          isLoading: false,
        });
        return;
      }
    }
  };

  getPlaceholder = () => {
    switch (this.state.queryType) {
      case 'USERNAME':
        return "ex. 'john.smith'";
      case 'EMAIL':
        return "ex. 'john.smith@email.com'";
      case 'USER ID':
        return "ex. 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'";
    }
  };

  getQueryTypeHelper = () => {
    switch (this.state.queryType) {
      case 'USERNAME':
      case 'EMAIL':
      case 'USER ID':
        return `${this.state.queryType} (lowercase)`;
      case 'FIRST/LAST NAME':
        return `${this.state.queryType} (case-sensitive)`;
    }
  };

  render() {
    const {
      queriedUser,
      showUserInfo,
    } = this.state;
    const isNotExternal = this.props.user.userType !== 'EXTERNAL';

    // Add certified info data into the top level of queriedUser
    if(queriedUser.certifiedInfo) {
      queriedUser.documentType = queriedUser.certifiedInfo.documentType;
      queriedUser.documentNumber = queriedUser.certifiedInfo.documentNumber;
      queriedUser.expirationDate = queriedUser.certifiedInfo.expirationDate;
      queriedUser.address = queriedUser.certifiedInfo.address;
      queriedUser.phoneNumber = queriedUser.certifiedInfo.phoneNumber;
    }

    let userInfo =
      queriedUser &&
      createUserInfoCard(
        queriedUser,
        this.state.windowWidth,
        this.props.user.userType,
        this.handleAcceptPI,
      );

    const isSingleShopResult = queriedUser?.userShops?.length === 1;

    let shopInfoAccordions =
      queriedUser &&
      queriedUser.userShops
        .sort((a, b) => a.shopName.localeCompare(b.shopName))
        .map((shop: Shop) => {
          return (
            <ShopInfoAccordion
              key={shop.shopID}
              shop={shop}
              getShop={(query?: string, queryType?: string) =>
                this.handleSubmit(undefined, query, queryType)
              }
              isSingleShopResult={isSingleShopResult}
              isLoading={this.state.isLoading}
              setIsLoading={(isLoading: boolean) => {
                this.setState({ isLoading: isLoading })
              }}
            />
          );
        });

    return (
      <>
        <Header context="User Functions" title="Get User Summary" />
        <FloatingButton
          onClick={() => {
            if (topRef && topRef.current) {
              topRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }}
        />
        <div className="l-container" ref={topRef}>
          <form>
            <div className="c-field">
              <label htmlFor="queryType" className="c-field__label">
                Query Type
              </label>
              <div className="c-select">
                <select
                  onChange={this.handleSelectionChange}
                  id="queryType"
                  value={this.state.queryType}
                >
                  {(['USERNAME', 'EMAIL', 'USER ID', 'FIRST/LAST NAME']).map(
                    (type, i) => {
                      return <option key={i}>{type}</option>;
                    }
                  )}
                </select>
              </div>
            </div>
            <div
              className={`c-field ${
                this.state.queryError && 'u-margin-bottom-none'
              }`}
            >
              <label
                htmlFor="query"
                className="c-field__label u-margin-top-large"
              >
                {this.getQueryTypeHelper()}
              </label>

              {this.state.queryType === 'FIRST/LAST NAME' ? (
                <FullNameForm
                  handleSubmit={() => {
                    if (this.validateForm()) {
                      this.handleSubmit();
                    }
                  }}
                  handleChange={this.handleFullNameChange}
                />
              ) : (
                <>
                  <FieldWithHistory
                    fieldId="query"
                    searchKey={this.state.queryType}
                    selected={this.state.query}
                    onInputChange={this.handleChange}
                    onChange={this.handleChange}
                    placeholder={this.getPlaceholder()}
                    onEnter={() => {
                      if (this.validateForm()) this.handleSubmit();
                    }}
                  />
                  {this.state.queryError && (
                    <div className="u-text-error u-margin-left-large">
                      <small>{this.state.queryError}</small>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="c-field">
              <Switch
                isChecked={this.state.includeLog}
                states={{
                  active: 'Include User Log',
                  inactive: 'Include User Log',
                }}
                handleCheck={(e) =>
                  this.setState({
                    includeLog: e,
                  })
                }
              />
            </div>
            {this.state.includeLog && (
              <div className="c-field">
                <label
                  htmlFor="query"
                  className="c-field__label u-margin-top-large"
                >
                  Date Range
                </label>
                <CustomDatePicker
                  rangePicked={this.state.rangePicked}
                  setRangePicked={(newRange: DateRangeOptions) =>
                    this.setState({ rangePicked: newRange })
                  }
                />
              </div>
            )}

            <div className="c-field">
              <LoaderButton
                disabled={!this.validateForm()}
                isLoading={this.state.isLoading}
                text="Get Summary"
                loadingText="Searching..."
                onClick={this.handleSubmit}
              />
            </div>
          </form>
        </div>

        {this.state.showSelectUser && (
          <FadeIn visible={!this.state.isLoading && this.state.showSelectUser}>
            <div>
              <h2>{this.state.queriedUsersFiltered.length} result(s) found:</h2>
            </div>
            <div className="c-field u-margin-top-small u-margin-right-small">
              <label htmlFor="filter" className="c-field__label u-is-vishidden">
                Filter
              </label>
              <input
                type="text"
                id="filter"
                maxLength={50}
                className="c-input u-font-mono"
                placeholder="Filter (Username, User ID, Email, First/Last Name)"
                value={this.state.filter}
                onChange={this.handleChangeFilter}
              />
              <i className="c-field__input-icon fal fa-search" />
            </div>
            {this.state.queriedUsersFiltered.map((user: QueriedUser) => {
              return (
                <div className="c-box" key={user.userID}>
                  <div className="l-flex-wrap">
                    <section className="c-section-alt l-flex-wrap">
                      <div className="c-section-alt__content">
                        <label className="c-section-alt__label">Username</label>
                        <span className="c-section-alt__value u-font-mono">
                          {user.userName}
                        </span>
                      </div>
                      <div className="c-section-alt__content">
                        <label className="c-section-alt__label">ID</label>
                        <span className="c-section-alt__value u-font-mono">
                          {user.userID}
                        </span>
                      </div>
                      <div className="c-section-alt__content">
                        <label className="c-section-alt__label">Email</label>
                        <span className="c-section-alt__value">
                          {user.email}
                        </span>
                      </div>
                      <div className="c-section-alt__content">
                        <label className="c-section-alt__label">
                          Full Name
                        </label>
                        <span className="c-section-alt__value u-font-mono">
                          {`${user.firstName} ${user.lastName}`}
                        </span>
                      </div>
                    </section>
                    <button
                      className="c-btn"
                      onClick={() => {
                        this.handleSubmit(
                          undefined,
                          undefined,
                          undefined,
                          user.userID
                        );
                      }}
                    >
                      <div className="c-btn__inner">Get Summary</div>
                    </button>
                  </div>
                </div>
              );
            })}
          </FadeIn>
        )}
        <FadeIn visible={!this.state.isLoading && showUserInfo}>
          <h1>Basic Info</h1>
          <hr />
          {isNotExternal && <CopyData info={queriedUser} />}
          <div className="u-margin-left-none">
            <div className="c-card">
              <h2 className="c-card__title">
                User's Name: &nbsp;&nbsp;{queriedUser.firstName}{' '}
                {queriedUser.lastName}
              </h2>
              <div className="c-card__description">
                {this.state.windowWidth < 1220 ? (
                  userInfo
                ) : (
                  <table className="card-table">
                    <colgroup>
                      <col className="card-table-col-labels-20" />
                      <col />
                    </colgroup>
                    <tbody>{userInfo}</tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
          <div className="u-margin-top-xlarge">
            <h1>User Shops</h1>
            <hr />
            {shopInfoAccordions}
          </div>
        </FadeIn>

        <FadeIn visible={!this.state.isLoading && this.state.showLog}>
          <h1>User Log</h1>
          <hr />
          <Accordion
            title={`User Log for ${this.state.queriedUser.userName}`}
            key={`${this.state.query}-log`}
            icon={<span className="material-icons">description</span>}
            maxHeightOverride={'fit-content'}
          >
            <div className="u-text-center">
              {queriedUser && (
                <>
                  <h2>
                    Log Table For User:{' '}
                    <span className="u-font-mono">{`${queriedUser.userName} ${isNotExternal ? `(${queriedUser.userID})` : ''}`}</span>
                  </h2>
                </>
              )}
            </div>
            <GenericTable
              id="SHOP_LOGS-table"
              data={this.state.log || []}
              filterKeys={['actionMessage', 'actionCode', 'actionDate', 'vin']}
              columnDefs={isNotExternal ? baseLogColumns.slice(1) : baseLogColumns.slice(1, 4)}
              defaultSorted={[
                {
                  id: 'actionDate',
                  desc: true,
                },
              ]}
              savedColumnsId={'fullLogTableColumns'}
              subComponentRenderer={isNotExternal ? LogSubcomponent: ExternalLogSubcomponent} // External Support Portal Phase 1. Adjust to user ExternalLogSubcomponent without IDs and with properly populated fields in future phase.
              getTrProps={(state: any, rowInfo: any, column: any) => {
                if (rowInfo) {
                  if (rowInfo.original.actionCode === 'UPDATE_USER_INFO') {
                    return {
                      className: 'hide-expander',
                    };
                  }
                }
                return {};
              }}
            />
          </Accordion>
        </FadeIn>
      </>
    );
  }
}
